import {
  Alert,
  Autocomplete,
  AutocompleteItem,
  Avatar,
  Badge,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDebouncedValue, useHotkeys, useMediaQuery } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText,
  Paperclip,
  Photo,
  Calendar,
  Clock,
  Eye,
  Link as LinkIcon,
  Plus,
  AlertCircle,
} from 'tabler-icons-react';

import { useUploadAttachment } from 'api/attachments/uploadAttachment';
import { useCreateIssueLink } from 'api/issueLinks/createIssueLink';
import { useCreateIssue } from 'api/issues/createIssue';
import { useSearchIssues } from 'api/issues/searchIssues';
import { useGetProject } from 'api/projects/getProject';
import { useGetProjectEpics } from 'api/projects/getProjectEpics';
import { useGetProjects } from 'api/projects/getProjects';
import { useGetSprints } from 'api/sprints/getSprints';
import { useUpdateSprint } from 'api/sprints/updateSprint';
import BulkCreateIssues from 'components/BulkCreateIssues/BulkCreateIssues';
import { ComponentSelector } from 'components/Components';
import { CustomFieldsSection } from 'components/CustomFields';
import IssueTemplateSelector from 'components/IssueTemplateSelector/IssueTemplateSelector';
import { LabelSelector } from 'components/Labels';
import { VersionSelector } from 'components/Versions';
import { useUser } from 'hooks/useUser';
import { Issue, IssueLink, IssueTemplate, ProjectEpic, Sprint, User } from 'types';
import { getInitials } from 'utils/getInitials';

type QuickCreateIssueProps = {
  /**
   * When provided (e.g. from Layout Header), used instead of useOutletContext.
   * Header is not under Outlet so it needs user passed in.
   */
  user?: User | null;
  children:
    | React.ReactNode
    | ((props: {
        open: () => void;
        openBulkCreate: () => void;
      }) => React.ReactNode);
};

interface SelectItemProps extends React.ComponentPropsWithoutRef<'div'> {
  icon: React.ReactNode;
  label: string;
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ icon, label, ...rest }, ref) => (
    <div ref={ref} {...rest}>
      <Group noWrap spacing={12}>
        {icon}
        <Text>{label}</Text>
      </Group>
    </div>
  )
);
SelectItem.displayName = 'SelectItem';

// Draft storage key prefix
const DRAFT_STORAGE_PREFIX = 'quickCreateIssue_draft_';

type FormValues = {
  title: string;
  description: string;
  type: Issue['type'];
  priority: Issue['priority'];
  projectId: string;
  assignee: string | null;
  epicLink: string | null;
  parentIssue: string | null;
  labels: string[];
  components: string[];
  fixVersions: string[];
  affectsVersions: string[];
  environment: 'development' | 'staging' | 'production' | null;
  dueDate: Date | null;
  watchers: string[];
  originalEstimateHours: number | null;
  timeRemainingHours: number | null;
  sprintId: string | null;
  customFields: Record<string, unknown>;
  linkedIssue: string | null;
  linkType: IssueLink['linkType'] | null;
};

function QuickCreateIssue({ user: userProp, children }: QuickCreateIssueProps) {
  const [opened, setOpened] = useState(false);
  const [bulkCreateOpened, setBulkCreateOpened] = useState(false);
  const [createAnother, setCreateAnother] = useState(false);
  const [issueKeyPreview, setIssueKeyPreview] = useState<string>('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingLinks, setPendingLinks] = useState<
    Array<{ targetIssue: string; linkType: IssueLink['linkType'] }>
  >([]);
  const [duplicateIssues, setDuplicateIssues] = useState<Issue[]>([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const userContext = useUser();
  const user = userProp !== undefined ? userProp : userContext?.user;
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const orgId = user?.org?._id;
  const { data: projectsData } = useGetProjects(orgId);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get project based on selected projectId (from URL or form)
  const [selectedProjectId, setSelectedProjectId] = useState<string>(urlProjectId || '');
  const { data: project } = useGetProject(selectedProjectId);
  const { data: epics = [] } = useGetProjectEpics(selectedProjectId);
  const { data: sprints = [] } = useGetSprints(selectedProjectId);
  const updateSprintMutation = useUpdateSprint();

  // Parent issue search
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [debouncedParentSearch] = useDebouncedValue(parentSearchQuery, 300);

  // Duplicate detection - search for similar titles
  const [titleSearchQuery, setTitleSearchQuery] = useState('');
  const [debouncedTitleSearch] = useDebouncedValue(titleSearchQuery, 500);

  // Issue linking search; selectedLinkType is used as default when adding a linked issue
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [debouncedLinkSearch] = useDebouncedValue(linkSearchQuery, 300);
  const [selectedLinkType, setSelectedLinkType] = useState<IssueLink['linkType'] | null>(null);

  // Load draft from localStorage
  const loadDraft = useCallback((projectId: string): Partial<FormValues> | null => {
    try {
      const draftKey = `${DRAFT_STORAGE_PREFIX}${projectId}`;
      const draft = localStorage.getItem(draftKey);
      if (draft) {
        const parsed = JSON.parse(draft) as unknown;
        return parsed as Partial<FormValues>;
      }
    } catch {
      // Ignore errors
    }
    return null;
  }, []);

  // Save draft to localStorage
  const saveDraft = useCallback((projectId: string, values: Record<string, unknown>) => {
    try {
      const draftKey = `${DRAFT_STORAGE_PREFIX}${projectId}`;
      localStorage.setItem(draftKey, JSON.stringify(values));
    } catch {
      // Ignore errors
    }
  }, []);

  // Clear draft
  const clearDraft = useCallback((projectId: string) => {
    try {
      const draftKey = `${DRAFT_STORAGE_PREFIX}${projectId}`;
      localStorage.removeItem(draftKey);
    } catch {
      // Ignore errors
    }
  }, []);

  // Get smart defaults (last used values per project)
  const getSmartDefaults = useCallback(
    (projectId: string) => {
      const defaults: Record<string, unknown> = {
        type: 'task',
        priority: 'medium',
      };
      try {
        const defaultsKey = `quickCreateIssue_defaults_${projectId}`;
        const saved = localStorage.getItem(defaultsKey);
        if (saved) {
          const parsed = JSON.parse(saved) as unknown;
          const typedParsed = parsed as Partial<FormValues>;
          Object.assign(defaults, typedParsed);
        }
      } catch {
        // Ignore errors
      }
      return defaults;
    },
    []
  );

  // Save smart defaults
  const saveSmartDefaults = useCallback((projectId: string, values: Record<string, unknown>) => {
    try {
      const defaultsKey = `quickCreateIssue_defaults_${projectId}`;
      const defaults = {
        type: values.type || 'task',
        priority: values.priority || 'medium',
      };
      localStorage.setItem(defaultsKey, JSON.stringify(defaults));
    } catch {
      // Ignore errors
    }
  }, []);

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      type: 'task' as Issue['type'],
      priority: 'medium' as Issue['priority'],
      projectId: urlProjectId || '',
      assignee: null as string | null,
      epicLink: null as string | null,
      parentIssue: null as string | null,
      labels: [] as string[],
      components: [] as string[],
      fixVersions: [] as string[],
      affectsVersions: [] as string[],
      environment: null as 'development' | 'staging' | 'production' | null,
      dueDate: null as Date | null,
      watchers: [] as string[],
      originalEstimateHours: null as number | null,
      timeRemainingHours: null as number | null,
      sprintId: null as string | null,
      customFields: {} as Record<string, unknown>,
      linkedIssue: null as string | null,
      linkType: null as IssueLink['linkType'] | null,
    },
    validate: {
      title: (value) => (value.trim() ? null : 'Title is required'),
      projectId: (value) => (value ? null : 'Project is required'),
      dueDate: (value) => (value ? null : 'Due date is required'),
      parentIssue: (value, values) =>
        values.type === 'sub-task' && !value
          ? 'Parent issue is required for sub-tasks'
          : null,
      originalEstimateHours: (value) =>
        value !== null && value < 0 ? 'Estimate must be positive' : null,
      timeRemainingHours: (value) =>
        value !== null && value < 0 ? 'Remaining time must be positive' : null,
    },
  });

  // Rich text editor for description
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder: 'Add a description...',
      }),
    ],
    content: form.values.description,
    onUpdate: ({ editor: tiptap }) => {
      form.setFieldValue('description', tiptap.getHTML());
    },
  });

  // Update editor when form description changes externally
  useEffect(() => {
    if (editor && form.values.description !== editor.getHTML()) {
      editor.commands.setContent(form.values.description || '');
    }
  }, [form.values.description, editor]);

  // Calculate issue key preview
  useEffect(() => {
    if (selectedProjectId && project?.key) {
      const issueNumber = (project.issueCounter || 0) + 1;
      setIssueKeyPreview(`${project.key}-${issueNumber}`);
    } else {
      setIssueKeyPreview('');
    }
  }, [selectedProjectId, project]);

  // Duplicate detection - search for similar titles
  const duplicateSearchJql = useMemo(() => {
    if (!debouncedTitleSearch.trim() || !selectedProjectId || debouncedTitleSearch.length < 3) {
      return '';
    }
    return `title ~ "${debouncedTitleSearch.trim()}" AND project = "${selectedProjectId}"`;
  }, [debouncedTitleSearch, selectedProjectId]);

  const { data: duplicateSearchResults = [] } = useSearchIssues(
    {
      jql: duplicateSearchJql,
      projectId: selectedProjectId,
    },
    !!duplicateSearchJql && debouncedTitleSearch.length >= 3
  );

  // Sync title search query with form title
  useEffect(() => {
    setTitleSearchQuery(form.values.title);
  }, [form.values.title]);

  // Stable key to avoid effect re-run when query disabled (new [] ref each render).
  const duplicateResultsKey = duplicateSearchResults.length > 0
    ? duplicateSearchResults.map((i: Issue) => i._id).join(',')
    : '';

  // Check for duplicates when title changes
  useEffect(() => {
    if (form.values.title.trim().length >= 3 && duplicateSearchResults.length > 0) {
      const similarIssues = duplicateSearchResults.filter(
        (issue: Issue) =>
          issue.title.toLowerCase().includes(form.values.title.toLowerCase()) ||
          form.values.title.toLowerCase().includes(issue.title.toLowerCase())
      );
      if (similarIssues.length > 0) {
        setDuplicateIssues(similarIssues.slice(0, 5)); // Show max 5 duplicates
        setShowDuplicateWarning(true);
      } else {
        setShowDuplicateWarning(false);
        setDuplicateIssues([]);
      }
    } else {
      setShowDuplicateWarning(false);
      setDuplicateIssues([]);
    }
    // duplicateSearchResults omitted: new [] ref when query disabled causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.title, duplicateResultsKey]);

  // Issue linking search JQL
  const linkSearchJql = useMemo(() => {
    if (!debouncedLinkSearch.trim() || !selectedProjectId || debouncedLinkSearch.length < 2) {
      return '';
    }
    const searchTerm = debouncedLinkSearch.trim();
    if (/^[A-Z]+-\d+$/i.test(searchTerm)) {
      return `jiraKey ~ "${searchTerm}" AND project = "${selectedProjectId}"`;
    }
    return `title ~ "${searchTerm}" AND project = "${selectedProjectId}"`;
  }, [debouncedLinkSearch, selectedProjectId]);

  const { data: linkSearchResults = [] } = useSearchIssues(
    {
      jql: linkSearchJql,
      projectId: selectedProjectId,
    },
    !!linkSearchJql && debouncedLinkSearch.length >= 2
  );

  const linkIssueOptions = useMemo<AutocompleteItem[]>(
    () =>
      linkSearchResults.map((issue: Issue) => ({
        value: issue._id,
        label: `${issue.jiraKey || issue._id.slice(-8)}: ${issue.title}`,
      })),
    [linkSearchResults]
  );

  // Parent issue search JQL
  const parentIssueJql = useMemo(() => {
    if (!debouncedParentSearch.trim() || !selectedProjectId || form.values.type !== 'sub-task') {
      return '';
    }
    const searchTerm = debouncedParentSearch.trim();
    if (/^[A-Z]+-\d+$/i.test(searchTerm)) {
      return `jiraKey ~ "${searchTerm}" AND project = "${selectedProjectId}" AND type != "sub-task"`;
    }
    return `title ~ "${searchTerm}" AND project = "${selectedProjectId}" AND type != "sub-task"`;
  }, [debouncedParentSearch, selectedProjectId, form.values.type]);

  const { data: parentIssueResults = [] } = useSearchIssues(
    {
      jql: parentIssueJql,
      projectId: selectedProjectId,
    },
    !!parentIssueJql && debouncedParentSearch.length >= 2 && form.values.type === 'sub-task'
  );

  const parentIssueOptions = useMemo<AutocompleteItem[]>(
    () =>
      parentIssueResults.map((issue: Issue) => ({
        value: issue._id,
        label: `${issue.jiraKey || issue._id.slice(-8)}: ${issue.title}`,
      })),
    [parentIssueResults]
  );

  // On project page (URL has projectId), keep selected project and form in sync for "+ Create"
  useEffect(() => {
    if (urlProjectId) {
      setSelectedProjectId(urlProjectId);
      form.setFieldValue('projectId', urlProjectId);
    }
  }, [urlProjectId, form]);

  // Update selected project when form changes (e.g. user picks different project)
  useEffect(() => {
    if (form.values.projectId && !urlProjectId) {
      setSelectedProjectId(form.values.projectId);
    }
  }, [form.values.projectId, urlProjectId]);

  // Load draft and smart defaults when modal opens
  const effectiveProjectIdWhenOpened = opened
    ? (urlProjectId || selectedProjectId)
    : '';
  useEffect(() => {
    if (opened && effectiveProjectIdWhenOpened) {
      const projectId = effectiveProjectIdWhenOpened;
      setSelectedProjectId(projectId);
      const draft = loadDraft(projectId);
      const defaults = getSmartDefaults(projectId);
      if (draft) {
        const draftValues: Partial<FormValues> = draft;
        form.setValues({
          ...defaults,
          ...draftValues,
          projectId,
        });
        const draftDescription = draftValues.description;
        if (draftDescription && typeof draftDescription === 'string' && editor) {
          editor.commands.setContent(draftDescription);
        }
      } else {
        form.setValues({
          ...defaults,
          projectId,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, effectiveProjectIdWhenOpened, loadDraft, getSmartDefaults]);

  // Auto-save draft when form values change
  useEffect(() => {
    if (opened && selectedProjectId && form.values.title) {
      const draft = {
        title: form.values.title,
        description: form.values.description,
        type: form.values.type,
        priority: form.values.priority,
        assignee: form.values.assignee,
        epicLink: form.values.epicLink,
        parentIssue: form.values.parentIssue,
        labels: form.values.labels,
        components: form.values.components,
        fixVersions: form.values.fixVersions,
        affectsVersions: form.values.affectsVersions,
        environment: form.values.environment,
        dueDate: form.values.dueDate,
        watchers: form.values.watchers,
        originalEstimateHours: form.values.originalEstimateHours,
        timeRemainingHours: form.values.timeRemainingHours,
        sprintId: form.values.sprintId,
        customFields: form.values.customFields,
      };
      saveDraft(selectedProjectId, draft);
    }
  }, [opened, selectedProjectId, form.values, saveDraft]);

  // Reset form when modal closes (unless createAnother is checked)
  useEffect(() => {
    if (!opened && !createAnother) {
      if (selectedProjectId) {
        clearDraft(selectedProjectId);
      }
      form.reset();
      if (editor) {
        editor.commands.setContent('');
      }
      setIssueKeyPreview('');
      setCreateAnother(false);
    } else if (opened && createAnother) {
      // Reset form but keep project
      const { projectId } = form.values;
      const defaults = getSmartDefaults(projectId);
      form.setValues({
        ...defaults,
        projectId,
        title: '',
        description: '',
        assignee: null,
        epicLink: null,
        parentIssue: null,
        labels: [],
        components: [],
        fixVersions: [],
        affectsVersions: [],
        environment: null,
        dueDate: null,
        watchers: [],
        originalEstimateHours: null,
        timeRemainingHours: null,
        sprintId: null,
        customFields: {},
      });
      if (editor) {
        editor.commands.setContent('');
      }
      setParentSearchQuery('');
      setCreateAnother(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, createAnother, selectedProjectId, clearDraft, getSmartDefaults]);

  // Reset parent issue when type changes from sub-task
  useEffect(() => {
    if (form.values.type !== 'sub-task' && form.values.parentIssue) {
      form.setFieldValue('parentIssue', null);
      setParentSearchQuery('');
    }
  }, [form.values.type, form.values.parentIssue, form]);

  // File upload mutation (will be created after issue is created)
  const [createdIssueId, setCreatedIssueId] = useState<string | null>(null);
  const uploadAttachmentMutation = useUploadAttachment(createdIssueId || '');

  // Issue link creation mutation
  const createIssueLinkMutation = useCreateIssueLink(createdIssueId || '');

  // Handle file uploads after issue creation
  useEffect(() => {
    if (createdIssueId && pendingFiles.length > 0) {
      pendingFiles.forEach((file) => {
        uploadAttachmentMutation.mutate(file, {
          onError: (error) => {
            console.error('Failed to upload attachment:', error);
          },
        });
      });
      setPendingFiles([]);
    }
  }, [createdIssueId, pendingFiles, uploadAttachmentMutation]);

  // Handle issue linking after issue creation
  useEffect(() => {
    if (createdIssueId && pendingLinks.length > 0) {
      pendingLinks.forEach((link) => {
        createIssueLinkMutation.mutate(
          {
            sourceIssue: createdIssueId,
            targetIssue: link.targetIssue,
            linkType: link.linkType,
          },
          {
            onError: (error) => {
              console.error('Failed to create issue link:', error);
            },
          }
        );
      });
      setPendingLinks([]);
    }
  }, [createdIssueId, pendingLinks, createIssueLinkMutation]);

  const createIssueMutation = useCreateIssue(orgId, (createdIssue) => {
    // Store created issue ID for post-creation operations
    if (createdIssue?._id) {
      setCreatedIssueId(createdIssue._id);
      // Reset after a delay to allow post-creation operations
      setTimeout(() => {
        setCreatedIssueId(null);
      }, 5000);
    }

    // Handle sprint assignment after issue creation
    const { sprintId } = form.values;

    if (sprintId && createdIssue?._id) {
      const sprint = sprints.find((s: Sprint) => s._id === sprintId);
      if (sprint) {
        const updatedIssueIds = Array.from(
          new Set([...sprint.issueIds, createdIssue._id])
        );
        updateSprintMutation.mutate({
          sprintId,
          issueIds: updatedIssueIds,
        });
      }
    }

    // Handle issue linking if specified (use selectedLinkType as fallback default)
    const linkTypeToUse =
      form.values.linkType || selectedLinkType || 'relates to';
    if (form.values.linkedIssue && linkTypeToUse && createdIssue?._id) {
      setPendingLinks([
        {
          targetIssue: form.values.linkedIssue,
          linkType: linkTypeToUse,
        },
      ]);
    }

    if (createAnother) {
      // Save smart defaults
      if (form.values.projectId) {
        saveSmartDefaults(form.values.projectId, form.values);
      }
      // Reset form for next issue
      const { projectId } = form.values;
      const defaults = getSmartDefaults(projectId);
      form.setValues({
        ...defaults,
        projectId,
        title: '',
        description: '',
        assignee: null,
        epicLink: null,
        parentIssue: null,
        labels: [],
        components: [],
        fixVersions: [],
        affectsVersions: [],
        environment: null,
        dueDate: null,
        watchers: [],
        originalEstimateHours: null,
        timeRemainingHours: null,
        sprintId: null,
        customFields: {},
      });
      if (editor) {
        editor.commands.setContent('');
      }
      setParentSearchQuery('');
      setCreateAnother(false);
    } else {
      // Clear draft and close modal
      if (selectedProjectId) {
        clearDraft(selectedProjectId);
        saveSmartDefaults(selectedProjectId, form.values);
      }
      form.reset();
      if (editor) {
        editor.commands.setContent('');
      }
      setPendingFiles([]);
      setPendingLinks([]);
      setCreatedIssueId(null);
      setOpened(false);
    }
  });

  const projectOptions = projectsData?.projects.map((p) => ({
    value: p._id,
    label: p.name,
  })) || [];

  const members = useMemo(() => {
    if (project?.members) {
      return project.members;
    }
    return [];
  }, [project]);

  const assigneeOptions = useMemo(
    () =>
      members.map((member) => ({
        icon: (
          <Avatar radius="xl" size="sm" color="violet.7">
            {getInitials(member.firstName, member.lastName)}
          </Avatar>
        ),
        label: member.email
          ? `${member.firstName} ${member.lastName} (${member.email})`
          : `${member.firstName} ${member.lastName}`,
        value: member._id,
      })),
    [members]
  );

  const watcherOptions = useMemo(
    () =>
      members.map((member) => ({
        value: member._id,
        label: member.email
          ? `${member.firstName} ${member.lastName} (${member.email})`
          : `${member.firstName} ${member.lastName}`,
      })),
    [members]
  );

  const epicOptions = epics.map((epic: ProjectEpic) => ({
    value: epic._id,
    label: epic.jiraKey ? `${epic.jiraKey} · ${epic.title}` : epic.title,
  }));

  // Active sprints for the project
  const activeSprintOptions = useMemo(
    () =>
      sprints
        .filter((sprint: Sprint) => sprint.status === 'active' || sprint.status === 'planned')
        .map((sprint: Sprint) => ({
          value: sprint._id,
          label: sprint.name,
        })),
    [sprints]
  );

  const handleTemplateSelect = (template: IssueTemplate) => {
    const { templateData } = template;
    form.setValues({
      title: templateData.title || '',
      description: templateData.description || '',
      type: template.issueType,
      priority: templateData.priority || 'medium',
      epicLink: templateData.epicLink || null,
      labels: templateData.labels || [],
      components: templateData.components || [],
      fixVersions: templateData.fixVersions || [],
      affectsVersions: templateData.affectsVersions || [],
      environment: templateData.environment || null,
    });
    if (editor && templateData.description) {
      editor.commands.setContent(templateData.description || '');
    }
  };

  const handleSubmit = (values: typeof form.values) => {
    if (!user?._id) {
      return;
    }
    const {
      epicLink,
      assignee,
      parentIssue,
      labels,
      components,
      fixVersions,
      affectsVersions,
      environment,
      dueDate,
      watchers,
      originalEstimateHours,
      timeRemainingHours,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- sprint after creation
      sprintId,
      customFields,
      ...rest
    } = values;

    // Convert hours to minutes for backend
    const originalEstimateMinutes =
      originalEstimateHours !== null ? originalEstimateHours * 60 : undefined;
    const timeRemainingMinutes =
      timeRemainingHours !== null ? timeRemainingHours * 60 : undefined;

    createIssueMutation.mutate({
      ...rest,
      epicLink: epicLink || undefined,
      assignee: assignee || undefined,
      parentIssue: parentIssue || undefined,
      labels: labels && labels.length > 0 ? labels : undefined,
      components: components && components.length > 0 ? components : undefined,
      fixVersions: fixVersions && fixVersions.length > 0 ? fixVersions : undefined,
      affectsVersions: affectsVersions && affectsVersions.length > 0 ? affectsVersions : undefined,
      environment: environment || undefined,
      dueDate: dueDate ? dueDate.toISOString() : undefined,
      watchers: watchers && watchers.length > 0 ? watchers : undefined,
      originalEstimateMinutes,
      timeRemainingMinutes,
      customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
      status: 'to do',
      project: values.projectId,
      createdBy: user._id,
    });

    // Note: Sprint assignment and issue linking will need to be handled after issue creation
    // via separate API calls if needed
  };

  // Keyboard shortcuts
  useHotkeys([
    [
      'mod + I',
      () => {
        if (user) {
          setOpened((prev) => !prev);
        }
      },
    ],
    [
      'mod + Enter',
      () => {
        if (opened && form.isValid() && user && formRef.current) {
          formRef.current.requestSubmit();
        }
      },
    ],
    [
      'Escape',
      () => {
        if (opened) {
          setOpened(false);
        }
      },
    ],
  ]);

  const openModal = useCallback(() => setOpened(true), []);
  const openBulkCreateModal = useCallback(() => setBulkCreateOpened(true), []);

  const showAdditionalFields = !!selectedProjectId;

  const triggerWithClick = (() => {
    if (typeof children === 'function') {
      return children({
        open: user ? openModal : () => {},
        openBulkCreate: user ? openBulkCreateModal : () => {},
      });
    }
    const child = React.Children.count(children) === 1 ? React.Children.toArray(children)[0] : null;
    if (user && child && React.isValidElement(child)) {
      return React.cloneElement(child, {
        ...(child.props as { onClick?: React.MouseEventHandler }),
        onClick: (e: React.MouseEvent) => {
          openModal();
          (child.props as { onClick?: React.MouseEventHandler })?.onClick?.(e);
        },
      } as Record<string, unknown>);
    }
    if (user) {
      return (
        <div
          onClick={openModal}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openModal();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Create issue"
          style={{ display: 'inline-block', cursor: 'pointer' }}
        >
          {children}
        </div>
      );
    }
    return <>{children}</>;
  })();

  return (
    <>
      {triggerWithClick}

      {user && (
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={
          <Group position="apart" style={{ width: '100%' }}>
            <Group spacing="xs">
              <Text weight={600}>Create Issue</Text>
              {issueKeyPreview && (
                <Tooltip label="Preview of issue key that will be created">
                  <Text size="sm" color="dimmed" weight={400}>
                    (
                    {issueKeyPreview}
                    )
                  </Text>
                </Tooltip>
              )}
            </Group>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => {
                setOpened(false);
                setBulkCreateOpened(true);
              }}
            >
              Bulk Import
            </Button>
          </Group>
        }
        size={isMobile ? 'full' : 'xl'}
        centered
        overlayOpacity={0.55}
        overlayBlur={3}
        closeOnClickOutside={false}
      >
        <form ref={formRef} onSubmit={form.onSubmit(handleSubmit)}>
          <ScrollArea style={{ maxHeight: isMobile ? '70vh' : '80vh' }} type="hover">
            <Grid gutter="md">
              {/* Left Column - Main Fields */}
              <Grid.Col span={isMobile ? 12 : 8}>
                <Stack spacing="md">
                  <Select
                    label="Project"
                    data={projectOptions}
                    placeholder="Select a project"
                    searchable
                    required
                    disabled={!!urlProjectId}
                    {...form.getInputProps('projectId')}
                  />

                  <div>
                    <div>
                      <TextInput
                        label="Issue Title"
                        placeholder="What needs to be done?"
                        required
                        autoFocus
                        {...form.getInputProps('title')}
                      />
                      {showDuplicateWarning && duplicateIssues.length > 0 && (
                        <Alert
                          icon={<AlertCircle size={16} />}
                          color="yellow"
                          mt="xs"
                          onClose={() => setShowDuplicateWarning(false)}
                          withCloseButton
                        >
                          <Text size="sm" weight={500} mb={4}>
                            Similar issues found:
                          </Text>
                          <Stack spacing={4}>
                            {duplicateIssues.map((issue: Issue) => (
                              <Text key={issue._id} size="xs">
                                •
                                {' '}
                                {issue.jiraKey || issue._id.slice(-8)}
                                :
                                {' '}
                                {issue.title}
                              </Text>
                            ))}
                          </Stack>
                        </Alert>
                      )}
                    </div>
                    {showDuplicateWarning && duplicateIssues.length > 0 && (
                      <Alert
                        icon={<AlertCircle size={16} />}
                        color="yellow"
                        mt="xs"
                        onClose={() => setShowDuplicateWarning(false)}
                        withCloseButton
                      >
                        <Text size="sm" weight={500} mb={4}>
                          Similar issues found:
                        </Text>
                        <Stack spacing={4}>
                          {duplicateIssues.map((issue: Issue) => (
                            <Text key={issue._id} size="xs">
                              •
                              {' '}
                              {issue.jiraKey || issue._id.slice(-8)}
                              :
                              {' '}
                              {issue.title}
                            </Text>
                          ))}
                        </Stack>
                      </Alert>
                    )}
                  </div>

                  <div>
                    <Text size="sm" weight={500} mb={5}>
                      Description
                    </Text>
                    <RichTextEditor editor={editor} style={{ minHeight: 150 }}>
                      <RichTextEditor.Toolbar sticky stickyOffset={60}>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Bold />
                          <RichTextEditor.Italic />
                          <RichTextEditor.Underline />
                          <RichTextEditor.Strikethrough />
                          <RichTextEditor.ClearFormatting />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.H1 />
                          <RichTextEditor.H2 />
                          <RichTextEditor.H3 />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.BulletList />
                          <RichTextEditor.OrderedList />
                          <RichTextEditor.Blockquote />
                        </RichTextEditor.ControlsGroup>

                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Link />
                          <RichTextEditor.Unlink />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Control
                            aria-label="Insert image"
                            onClick={() => {
                              // eslint-disable-next-line no-alert -- image URL input
                              const url = window.prompt('Image URL:');
                              if (url?.trim()) {
                                editor?.chain().focus().setImage({ src: url.trim() }).run();
                              }
                            }}
                          >
                            <Photo size={16} />
                          </RichTextEditor.Control>
                        </RichTextEditor.ControlsGroup>
                      </RichTextEditor.Toolbar>

                      <RichTextEditor.Content />
                    </RichTextEditor>
                  </div>

                  <Divider />

                  <Grid>
                    <Grid.Col span={6}>
                      <Select
                        label="Issue Type"
                        data={[
                          { value: 'task', label: 'Task' },
                          { value: 'story', label: 'Story' },
                          { value: 'bug', label: 'Bug' },
                          { value: 'epic', label: 'Epic' },
                          { value: 'sub-task', label: 'Sub-task' },
                        ]}
                        {...form.getInputProps('type')}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Select
                        label="Priority"
                        data={[
                          { value: 'low', label: 'Low' },
                          { value: 'medium', label: 'Medium' },
                          { value: 'high', label: 'High' },
                        ]}
                        {...form.getInputProps('priority')}
                      />
                    </Grid.Col>
                  </Grid>

                  {form.values.type === 'sub-task' && showAdditionalFields && (
                    <Autocomplete
                      label="Parent Issue"
                      placeholder="Search for parent issue..."
                      required
                      data={parentIssueOptions}
                      value={parentSearchQuery}
                      onChange={(value) => {
                        setParentSearchQuery(value);
                        const found = parentIssueOptions.find((opt) => opt.label === value);
                        if (found) {
                          form.setFieldValue('parentIssue', found.value);
                        } else if (!value) {
                          form.setFieldValue('parentIssue', null);
                        }
                      }}
                      onItemSubmit={(item) => {
                        form.setFieldValue('parentIssue', item.value);
                        const label = typeof item.label === 'string' ? item.label : '';
                        setParentSearchQuery(label);
                      }}
                      nothingFound="No issues found"
                      error={form.errors.parentIssue}
                    />
                  )}

                  {showAdditionalFields && (
                    <IssueTemplateSelector
                      projectId={selectedProjectId}
                      issueType={form.values.type}
                      onSelectTemplate={handleTemplateSelect}
                    />
                  )}
                </Stack>
              </Grid.Col>

              {/* Right Column - Additional Fields */}
              {!isMobile && showAdditionalFields && (
                <Grid.Col span={4}>
                  <Paper withBorder p="md" radius="md">
                    <Stack spacing="md">
                      <Text size="sm" weight={600}>
                        Additional Details
                      </Text>

                      <Select
                        label="Assignee"
                        itemComponent={SelectItem}
                        data={assigneeOptions}
                        icon={
                          assigneeOptions.find((item) => item.value === form.values.assignee)
                            ?.icon
                        }
                        placeholder="Unassigned"
                        clearable
                        searchable
                        nothingFound="No members found"
                        {...form.getInputProps('assignee')}
                      />

                      <MultiSelect
                        label="Watchers"
                        placeholder="Select watchers"
                        data={watcherOptions}
                        value={form.values.watchers}
                        onChange={(value) => form.setFieldValue('watchers', value)}
                        icon={<Eye size={16} />}
                        clearable
                        searchable
                        nothingFound="No members found"
                      />

                      {epicOptions.length > 0 && (
                        <Select
                          label="Epic"
                          placeholder="Link to epic"
                          data={epicOptions}
                          clearable
                          searchable
                          nothingFound="No matching epics"
                          {...form.getInputProps('epicLink')}
                        />
                      )}

                      {activeSprintOptions.length > 0 && (
                        <Select
                          label="Sprint"
                          placeholder="Assign to sprint"
                          data={activeSprintOptions}
                          clearable
                          searchable
                          nothingFound="No active sprints"
                          {...form.getInputProps('sprintId')}
                        />
                      )}

                      <DatePicker
                        label="Due Date"
                        placeholder="Select due date"
                        icon={<Calendar size={16} />}
                        required
                        minDate={new Date()}
                        {...form.getInputProps('dueDate')}
                      />

                      <Divider />

                      <Group spacing="xs" align="flex-end">
                        <NumberInput
                          label="Estimate"
                          placeholder="Hours"
                          icon={<Clock size={16} />}
                          min={0}
                          step={0.5}
                          precision={1}
                          style={{ flex: 1 }}
                          {...form.getInputProps('originalEstimateHours')}
                        />
                        <NumberInput
                          label="Remaining"
                          placeholder="Hours"
                          icon={<Clock size={16} />}
                          min={0}
                          step={0.5}
                          precision={1}
                          style={{ flex: 1 }}
                          {...form.getInputProps('timeRemainingHours')}
                        />
                      </Group>

                      {orgId && selectedProjectId && (
                        <>
                          <LabelSelector
                            orgId={orgId}
                            projectId={selectedProjectId}
                            value={form.values.labels}
                            onChange={(labels) => form.setFieldValue('labels', labels)}
                          />

                          <ComponentSelector
                            projectId={selectedProjectId}
                            orgId={orgId}
                            value={form.values.components}
                            onChange={(components) => form.setFieldValue('components', components)}
                          />

                          <VersionSelector
                            projectId={selectedProjectId}
                            orgId={orgId}
                            label="Fix Versions"
                            value={form.values.fixVersions}
                            onChange={(versions) => form.setFieldValue('fixVersions', versions)}
                          />

                          <VersionSelector
                            projectId={selectedProjectId}
                            orgId={orgId}
                            label="Affects Versions"
                            value={form.values.affectsVersions}
                            onChange={(versions) =>
                              form.setFieldValue('affectsVersions', versions)
                            }
                          />

                          <Select
                            label="Environment"
                            data={[
                              { value: 'development', label: 'Development' },
                              { value: 'staging', label: 'Staging' },
                              { value: 'production', label: 'Production' },
                            ]}
                            value={form.values.environment}
                            onChange={(value) =>
                              form.setFieldValue(
                                'environment',
                                (value as typeof form.values.environment) || null
                              )
                            }
                            placeholder="Select environment"
                            clearable
                          />

                          <CustomFieldsSection
                            form={
                              form as unknown as React.ComponentProps<
                                typeof CustomFieldsSection
                              >['form']
                            }
                            orgId={orgId}
                            projectId={selectedProjectId}
                            members={members}
                          />

                          <Divider />

                          {/* File Attachments */}
                          <div>
                            <Text size="sm" weight={500} mb={5}>
                              Attachments
                            </Text>
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                setPendingFiles((prev) => [...prev, ...files]);
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = '';
                                }
                              }}
                            />
                            <Button
                              leftIcon={<Paperclip size={16} />}
                              variant="light"
                              onClick={() => fileInputRef.current?.click()}
                              size="sm"
                            >
                              Add Files
                            </Button>
                            {pendingFiles.length > 0 && (
                              <Stack spacing={4} mt="xs">
                                {pendingFiles.map((file) => (
                                  <Group key={`file-${file.name}-${file.size}-${file.lastModified}`} spacing="xs">
                                    <FileText size={14} />
                                    <Text size="xs" style={{ flex: 1 }} truncate>
                                      {file.name}
                                    </Text>
                                    <Button
                                      size="xs"
                                      variant="subtle"
                                      color="red"
                                      onClick={() => {
                                        setPendingFiles((prev) =>
                                          prev.filter(
                                            (f) =>
                                              f.name !== file.name ||
                                              f.size !== file.size ||
                                              f.lastModified !== file.lastModified
                                          )
                                        );
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  </Group>
                                ))}
                              </Stack>
                            )}
                          </div>

                          {/* Issue Linking */}
                          <div>
                            <Text size="sm" weight={500} mb={5}>
                              Link Issue
                            </Text>
                            <Group spacing="xs" mb="xs">
                              <Select
                                placeholder="Link type"
                                data={[
                                  { value: 'blocks', label: 'Blocks' },
                                  { value: 'is blocked by', label: 'Is Blocked By' },
                                  { value: 'relates to', label: 'Relates To' },
                                  { value: 'duplicates', label: 'Duplicates' },
                                  { value: 'is duplicated by', label: 'Is Duplicated By' },
                                  { value: 'clones', label: 'Clones' },
                                  { value: 'is cloned by', label: 'Is Cloned By' },
                                ]}
                                value={form.values.linkType}
                                onChange={(value) => {
                                  const linkType = (value as IssueLink['linkType']) || null;
                                  form.setFieldValue('linkType', linkType);
                                  setSelectedLinkType(linkType);
                                }}
                                style={{ flex: 1 }}
                                clearable
                              />
                            </Group>
                            {form.values.linkType && (
                              <Autocomplete
                                placeholder="Search for issue to link..."
                                data={linkIssueOptions}
                                value={linkSearchQuery}
                                onChange={(value) => {
                                  setLinkSearchQuery(value);
                                  const found = linkIssueOptions.find(
                                    (opt) => opt.label === value
                                  );
                                  if (found) {
                                    form.setFieldValue('linkedIssue', found.value);
                                  } else if (!value) {
                                    form.setFieldValue('linkedIssue', null);
                                  }
                                }}
                                onItemSubmit={(item) => {
                                  form.setFieldValue('linkedIssue', item.value);
                                  const label =
                                    typeof item.label === 'string' ? item.label : '';
                                  setLinkSearchQuery(label);
                                }}
                                nothingFound="No issues found"
                                icon={<LinkIcon size={16} />}
                              />
                            )}
                            {form.values.linkedIssue && (
                              <Group spacing="xs" mt="xs">
                                <Badge color="blue" variant="light">
                                  {linkIssueOptions.find(
                                    (opt) => opt.value === form.values.linkedIssue
                                  )?.label || 'Linked'}
                                </Badge>
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  color="red"
                                  onClick={() => {
                                    form.setFieldValue('linkedIssue', null);
                                    form.setFieldValue('linkType', null);
                                    setLinkSearchQuery('');
                                  }}
                                >
                                  Remove
                                </Button>
                              </Group>
                            )}
                          </div>
                        </>
                      )}
                    </Stack>
                  </Paper>
                </Grid.Col>
              )}

              {/* Mobile: Show additional fields below */}
              {isMobile && showAdditionalFields && (
                <Grid.Col span={12}>
                  <Divider my="md" />
                  <Paper withBorder p="md" radius="md">
                    <Stack spacing="md">
                      <Text size="sm" weight={600}>
                        Additional Details
                      </Text>

                      <Select
                        label="Assignee"
                        itemComponent={SelectItem}
                        data={assigneeOptions}
                        icon={
                          assigneeOptions.find((item) => item.value === form.values.assignee)?.icon
                        }
                        placeholder="Unassigned"
                        clearable
                        searchable
                        nothingFound="No members found"
                        {...form.getInputProps('assignee')}
                      />

                      <MultiSelect
                        label="Watchers"
                        placeholder="Select watchers"
                        data={watcherOptions}
                        value={form.values.watchers}
                        onChange={(value) => form.setFieldValue('watchers', value)}
                        icon={<Eye size={16} />}
                        clearable
                        searchable
                        nothingFound="No members found"
                      />

                      {epicOptions.length > 0 && (
                        <Select
                          label="Epic"
                          placeholder="Link to epic"
                          data={epicOptions}
                          clearable
                          searchable
                          nothingFound="No matching epics"
                          {...form.getInputProps('epicLink')}
                        />
                      )}

                      {activeSprintOptions.length > 0 && (
                        <Select
                          label="Sprint"
                          placeholder="Assign to sprint"
                          data={activeSprintOptions}
                          clearable
                          searchable
                          nothingFound="No active sprints"
                          {...form.getInputProps('sprintId')}
                        />
                      )}

                      <DatePicker
                        label="Due Date"
                        placeholder="Select due date"
                        icon={<Calendar size={16} />}
                        required
                        {...form.getInputProps('dueDate')}
                        dropdownType="modal"
                      />

                      <Divider />

                      <Group spacing="xs" align="flex-end">
                        <NumberInput
                          label="Estimate"
                          placeholder="Hours"
                          icon={<Clock size={16} />}
                          min={0}
                          step={0.5}
                          precision={1}
                          style={{ flex: 1 }}
                          {...form.getInputProps('originalEstimateHours')}
                        />
                        <NumberInput
                          label="Remaining"
                          placeholder="Hours"
                          icon={<Clock size={16} />}
                          min={0}
                          step={0.5}
                          precision={1}
                          style={{ flex: 1 }}
                          {...form.getInputProps('timeRemainingHours')}
                        />
                      </Group>

                      {orgId && selectedProjectId && (
                        <>
                          <LabelSelector
                            orgId={orgId}
                            projectId={selectedProjectId}
                            value={form.values.labels}
                            onChange={(labels) => form.setFieldValue('labels', labels)}
                          />

                          <ComponentSelector
                            projectId={selectedProjectId}
                            orgId={orgId}
                            value={form.values.components}
                            onChange={(components) => form.setFieldValue('components', components)}
                          />

                          <VersionSelector
                            projectId={selectedProjectId}
                            orgId={orgId}
                            label="Fix Versions"
                            value={form.values.fixVersions}
                            onChange={(versions) => form.setFieldValue('fixVersions', versions)}
                          />

                          <VersionSelector
                            projectId={selectedProjectId}
                            orgId={orgId}
                            label="Affects Versions"
                            value={form.values.affectsVersions}
                            onChange={(versions) =>
                              form.setFieldValue('affectsVersions', versions)
                            }
                          />

                          <Select
                            label="Environment"
                            data={[
                              { value: 'development', label: 'Development' },
                              { value: 'staging', label: 'Staging' },
                              { value: 'production', label: 'Production' },
                            ]}
                            value={form.values.environment}
                            onChange={(value) =>
                              form.setFieldValue(
                                'environment',
                                (value as typeof form.values.environment) || null
                              )
                            }
                            placeholder="Select environment"
                            clearable
                          />

                          <CustomFieldsSection
                            form={
                              form as unknown as React.ComponentProps<
                                typeof CustomFieldsSection
                              >['form']
                            }
                            orgId={orgId}
                            projectId={selectedProjectId}
                            members={members}
                          />

                          <Divider />

                          {/* File Attachments */}
                          <div>
                            <Text size="sm" weight={500} mb={5}>
                              Attachments
                            </Text>
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                setPendingFiles((prev) => [...prev, ...files]);
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = '';
                                }
                              }}
                            />
                            <Button
                              leftIcon={<Paperclip size={16} />}
                              variant="light"
                              onClick={() => fileInputRef.current?.click()}
                              size="sm"
                            >
                              Add Files
                            </Button>
                            {pendingFiles.length > 0 && (
                              <Stack spacing={4} mt="xs">
                                {pendingFiles.map((file) => (
                                  <Group key={`file-${file.name}-${file.size}-${file.lastModified}`} spacing="xs">
                                    <FileText size={14} />
                                    <Text size="xs" style={{ flex: 1 }} truncate>
                                      {file.name}
                                    </Text>
                                    <Button
                                      size="xs"
                                      variant="subtle"
                                      color="red"
                                      onClick={() => {
                                        setPendingFiles((prev) =>
                                          prev.filter(
                                            (f) =>
                                              f.name !== file.name ||
                                              f.size !== file.size ||
                                              f.lastModified !== file.lastModified
                                          )
                                        );
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  </Group>
                                ))}
                              </Stack>
                            )}
                          </div>

                          {/* Issue Linking */}
                          <div>
                            <Text size="sm" weight={500} mb={5}>
                              Link Issue
                            </Text>
                            <Group spacing="xs" mb="xs">
                              <Select
                                placeholder="Link type"
                                data={[
                                  { value: 'blocks', label: 'Blocks' },
                                  { value: 'is blocked by', label: 'Is Blocked By' },
                                  { value: 'relates to', label: 'Relates To' },
                                  { value: 'duplicates', label: 'Duplicates' },
                                  { value: 'is duplicated by', label: 'Is Duplicated By' },
                                  { value: 'clones', label: 'Clones' },
                                  { value: 'is cloned by', label: 'Is Cloned By' },
                                ]}
                                value={form.values.linkType}
                                onChange={(value) => {
                                  const linkType = (value as IssueLink['linkType']) || null;
                                  form.setFieldValue('linkType', linkType);
                                  setSelectedLinkType(linkType);
                                }}
                                style={{ flex: 1 }}
                                clearable
                              />
                            </Group>
                            {form.values.linkType && (
                              <Autocomplete
                                placeholder="Search for issue to link..."
                                data={linkIssueOptions}
                                value={linkSearchQuery}
                                onChange={(value) => {
                                  setLinkSearchQuery(value);
                                  const found = linkIssueOptions.find(
                                    (opt) => opt.label === value
                                  );
                                  if (found) {
                                    form.setFieldValue('linkedIssue', found.value);
                                  } else if (!value) {
                                    form.setFieldValue('linkedIssue', null);
                                  }
                                }}
                                onItemSubmit={(item) => {
                                  form.setFieldValue('linkedIssue', item.value);
                                  const label =
                                    typeof item.label === 'string' ? item.label : '';
                                  setLinkSearchQuery(label);
                                }}
                                nothingFound="No issues found"
                                icon={<LinkIcon size={16} />}
                              />
                            )}
                            {form.values.linkedIssue && (
                              <Group spacing="xs" mt="xs">
                                <Badge color="blue" variant="light">
                                  {linkIssueOptions.find(
                                    (opt) => opt.value === form.values.linkedIssue
                                  )?.label || 'Linked'}
                                </Badge>
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  color="red"
                                  onClick={() => {
                                    form.setFieldValue('linkedIssue', null);
                                    form.setFieldValue('linkType', null);
                                    setLinkSearchQuery('');
                                  }}
                                >
                                  Remove
                                </Button>
                              </Group>
                            )}
                          </div>
                        </>
                      )}
                    </Stack>
                  </Paper>
                </Grid.Col>
              )}
            </Grid>
          </ScrollArea>

          <Group position="apart" mt="md">
            <Checkbox
              label="Create another"
              checked={createAnother}
              onChange={(e) => setCreateAnother(e.currentTarget.checked)}
            />
            <Group>
              <Button
                variant="subtle"
                onClick={() => setOpened(false)}
                type="button"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setOpened(false);
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!form.isValid() || createIssueMutation.isLoading}
                loading={createIssueMutation.isLoading}
                leftIcon={<Plus size={16} />}
              >
                Create Issue
              </Button>
            </Group>
          </Group>
        </form>
      </Modal>
      )}

      <BulkCreateIssues
        opened={bulkCreateOpened}
        onClose={() => setBulkCreateOpened(false)}
        defaultProjectId={selectedProjectId || undefined}
      />
    </>
  );
}

export default QuickCreateIssue;
